import SEOHead from "@/components/SEOHead";
import { Overline } from "@/components/Section";

const SECTIONS = [
  { id: "ownership",     label: "Ownership" },
  { id: "protected",     label: "Protected Materials" },
  { id: "permitted",     label: "Permitted Use" },
  { id: "prohibited",    label: "Prohibited Use" },
  { id: "trademarks",    label: "Trademarks & Branding" },
  { id: "dmca",          label: "DMCA Notice" },
  { id: "driver-data",   label: "Driver Application Data Ownership" },
  { id: "user-content",  label: "User-Submitted Content" },
  { id: "third-party",   label: "Third-Party Materials" },
  { id: "changes",       label: "Changes to This Policy" },
  { id: "contact",       label: "Contact" },
];

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-28 mb-12 pb-12 border-b border-border last:border-0 last:mb-0 last:pb-0">
      <h2 className="font-head text-xl md:text-2xl font-bold uppercase tracking-[0.12em] text-[#0A0A0A] mb-4">{title}</h2>
      <div className="space-y-4 text-foreground/75 text-base leading-relaxed">{children}</div>
    </section>
  );
}

export default function CopyrightPolicy() {
  return (
    <>
      <SEOHead
        title="Copyright Policy | Hylas Companion"
        description="Copyright Policy for Hylas Companion — all site content, branding, and materials are protected. Andrej Sasaroga DBA Hylas Companion."
        path="/copyright"
      />

      {/* Hero */}
      <section className="bg-[#0A0A0A] pt-40 pb-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <Overline light>Legal</Overline>
          <h1 className="font-head text-5xl sm:text-6xl font-bold uppercase tracking-[0.12em] text-white mt-5 leading-none">
            Copyright<br /><span className="text-primary">Policy</span>
          </h1>
          <p className="mt-6 text-white/50 text-sm">
            Effective Date: February 20, 2026 &nbsp;·&nbsp; Last Updated: October 30, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="lg:grid lg:grid-cols-[260px_1fr] lg:gap-16 xl:gap-24">

          {/* Sticky TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-28">
              <p className="font-head text-[10px] uppercase tracking-[0.35em] text-primary mb-4">Contents</p>
              <nav className="space-y-2">
                {SECTIONS.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="block text-sm text-foreground/55 hover:text-primary transition-colors duration-200 leading-snug"
                  >
                    {s.label}
                  </a>
                ))}
              </nav>
              <div className="mt-10 pt-8 border-t border-border">
                <p className="text-xs text-foreground/40 leading-relaxed">
                  DMCA notices: <a href="mailto:contact@hylascompanion.com" className="text-primary hover:underline">contact@hylascompanion.com</a>
                </p>
              </div>
            </div>
          </aside>

          {/* Body */}
          <article className="min-w-0">

            <Section id="ownership" title="Ownership">
              <p>
                All content published on this website — located at <strong>hylascompanion.us and hylascompanion.com</strong> — is owned by or licensed to <strong>Andrej Sasaroga DBA Hylas Companion</strong> ("Company," "we," "us," or "our"), unless otherwise indicated. This includes all text, images, video, audio, design elements, page layouts, interactive tools, calculators, databases, software code, and any other materials accessible through the Site.
              </p>
              <p>
                This content is protected under the copyright laws of the United States (Title 17 of the U.S. Code), international copyright treaties, and applicable laws of other jurisdictions.
              </p>
              <p>
                Copyright © {new Date().getFullYear()} Andrej Sasaroga DBA Hylas Companion. All rights reserved.
              </p>
            </Section>

            <Section id="protected" title="Protected Materials">
              <p>The following categories of content on this Site are protected by copyright and may not be reproduced, distributed, modified, or used without our express written permission:</p>

              <h3 className="font-head text-base uppercase tracking-[0.15em] text-[#0A0A0A] mt-6 mb-2">Written Content</h3>
              <p>
                All text on the Site, including but not limited to: position descriptions, pay package descriptions, FAQ responses, blog articles, city landing page content, calculators, legal pages, and all other written materials. This includes our <em>Trucking Guides No Fluff</em> blog series and all individual articles published under the Hylas Companion brand.
              </p>

              <h3 className="font-head text-base uppercase tracking-[0.15em] text-[#0A0A0A] mt-6 mb-2">Visual &amp; Design Assets</h3>
              <p>
                The visual design of the Site — including page layout, color scheme, typography choices, spacing, component design, and the overall aesthetic of the Hylas Companion brand presentation — constitutes protectable creative expression. Additionally, all original images generated for this Site (including AI-generated truck photographs and marketing visuals) are protected as original works.
              </p>

              <h3 className="font-head text-base uppercase tracking-[0.15em] text-[#0A0A0A] mt-6 mb-2">Interactive Tools &amp; Calculators</h3>
              <p>
                The Gross Pay Calculator, Settlement Calculator, and any other interactive tools featured on the Site — including their underlying logic, formulas, user interface design, and user experience flow — are proprietary works protected by copyright.
              </p>

              <h3 className="font-head text-base uppercase tracking-[0.15em] text-[#0A0A0A] mt-6 mb-2">Software &amp; Code</h3>
              <p>
                The source code, scripts, stylesheets, and underlying software of the Site are proprietary and protected. Unauthorized copying, decompiling, reverse engineering, or extraction of source code is strictly prohibited.
              </p>

              <h3 className="font-head text-base uppercase tracking-[0.15em] text-[#0A0A0A] mt-6 mb-2">Database &amp; Compiled Information</h3>
              <p>
                The compilation and arrangement of information in our driver application systems, city-specific job listings, pay package structures, and structured data presented on the Site constitutes a protectable database. Even if individual data points are in the public domain, the selection, arrangement, and presentation is an original work.
              </p>
            </Section>

            <Section id="permitted" title="Permitted Use">
              <p>You may access and view the Site content for your personal, non-commercial use subject to the following conditions:</p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>You may print or download individual pages for your personal reference only (e.g., reviewing a pay package or reading a blog article).</li>
                <li>You may share links to pages on the Site on social media, in text messages, or via email for informational purposes.</li>
                <li>Brief quotations of Site content (under 100 words) are permitted provided the quote is attributed to "Hylas Companion (hylascompanion.us / hylascompanion.com)" and linked to the source page.</li>
              </ul>
              <p>
                All permitted uses are for non-commercial, personal purposes only. Any commercial use requires our prior written consent.
              </p>
            </Section>

            <Section id="prohibited" title="Prohibited Use">
              <p>The following uses of Site content are expressly prohibited without our prior written permission:</p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>Reproducing, copying, or duplicating any substantial portion of Site content on another website, publication, or platform</li>
                <li>Scraping, harvesting, or systematically extracting content, data, or text from the Site using automated tools or scripts</li>
                <li>Creating derivative works based on Site content, including paraphrasing blog articles to create "similar" content for competing websites</li>
                <li>Using any Site content for commercial purposes — including republishing, selling, licensing, or using content to attract customers to a competing business</li>
                <li>Reproducing our interactive tools (calculators, forms, or data-driven features) in whole or in part on any other platform</li>
                <li>Removing or altering any copyright notices, watermarks, or attribution present on our materials</li>
                <li>Using any Hylas Companion branding, logos, or visual identity in a way that suggests endorsement, affiliation, or partnership without our written consent</li>
                <li>Training, fine-tuning, or otherwise using Site content as training data for artificial intelligence or machine learning systems without express written consent</li>
              </ul>
            </Section>

            <Section id="trademarks" title="Trademarks & Branding">
              <p>
                The name <strong>HYLAS COMPANION</strong>, the <strong>HYLAS</strong> brand mark, associated logos, taglines (including "Drive New Trucks. Earn More."), and all distinctive branding elements displayed on this Site are trademarks and trade dress of Andrej Sasaroga DBA Hylas Companion, whether registered or unregistered.
              </p>
              <p>
                Nothing on this Site grants any license or right to use our trademarks, service marks, or brand elements. Unauthorized use of our trademarks is strictly prohibited and may constitute trademark infringement under applicable law.
              </p>
              <p>
                References to third-party trademarks (such as truck manufacturer names like Peterbilt, Kenworth, Freightliner, Volvo, and International) are used for descriptive and informational purposes only and do not imply affiliation, endorsement, or sponsorship by those companies.
              </p>
            </Section>

            <Section id="dmca" title="DMCA Notice & Takedown Procedure">
              <p>
                We respect the intellectual property rights of others and expect users to do the same. If you believe that content on this Site infringes your copyright, please submit a DMCA takedown notice containing the following information:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>A physical or electronic signature of the copyright owner or authorized agent</li>
                <li>Identification of the copyrighted work you claim has been infringed</li>
                <li>Identification of the material on our Site that you claim is infringing, with sufficient detail for us to locate it (URL)</li>
                <li>Your contact information (address, phone, email)</li>
                <li>A statement that you have a good faith belief that the disputed use is not authorized by the copyright owner, its agent, or the law</li>
                <li>A statement, made under penalty of perjury, that the information in your notice is accurate and that you are the copyright owner or authorized to act on their behalf</li>
              </ul>
              <p>
                Send DMCA notices to: <a href="mailto:contact@hylascompanion.com" className="text-primary hover:underline">contact@hylascompanion.com</a> with the subject line "DMCA Notice."
              </p>
              <p>
                We will respond to valid DMCA notices promptly and remove infringing content in accordance with the Digital Millennium Copyright Act (17 U.S.C. § 512).
              </p>
              <p>
                Counter-notices may be submitted in response to a takedown. Repeat infringers will have their access to the Site terminated.
              </p>
            </Section>

            <Section id="driver-data" title="Driver Application Data Ownership">
              <p>
                The personal information provided by drivers in their Hylas Companion application — including name, address, CDL number, employment history, medical certificate, and consent records — is the personal property of the driver who submitted it. Hylas Companion does not claim ownership of driver-submitted PII.
              </p>
              <p>
                However, the <strong>Driver Qualification (DQ) Packet</strong> generated by Hylas Companion — a compiled, formatted, FMCSA §391.21-compliant document assembling the driver's application data with Hylas Companion's formatting, compliance language, and authorization documentation — is a derivative work and proprietary output of Hylas Companion, LLC. The DQ Packet document itself (as distinct from the underlying driver data it contains) is protected by copyright.
              </p>
              <p>
                Motor carriers who receive a Hylas Companion DQ Packet via the Carrier Hub are granted a limited, non-transferable license to:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>Place the packet into their Driver Qualification (DQ) file for DOT compliance purposes</li>
                <li>Share the packet with their safety director, DOT compliance officer, or third-party DQ auditors for the purpose of qualifying the specific driver named in the packet</li>
              </ul>
              <p>
                Motor carriers may NOT reproduce, resell, distribute, or transmit the DQ Packet to any party not involved in the qualification of the specific named driver without prior written consent from Hylas Companion.
              </p>
              <p>
                Drivers retain the right to request a copy of their DQ Packet by contacting <a href="mailto:contact@hylascompanion.com" className="text-primary hover:underline">contact@hylascompanion.com</a>.
              </p>
            </Section>

            <Section id="user-content" title="User-Submitted Content">
              <p>
                When you submit an application, contact form, or any other content through the Site, you represent that you own or have the right to submit the information provided. You grant Hylas Companion a limited, non-exclusive license to use submitted content solely for the purposes described in our <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a> (i.e., driver placement services).
              </p>
              <p>
                We do not claim ownership of information you submit as part of your driver application. Your personal and professional data remains yours, and we process it only as described in our Privacy Policy.
              </p>
            </Section>

            <Section id="third-party" title="Third-Party Materials">
              <p>
                Some images displayed on this Site are sourced from licensed third-party providers, including Unsplash, Pexels, and AI image generation platforms. These images are used under applicable licenses and do not transfer any copyright ownership to site visitors.
              </p>
              <p>
                References to or descriptions of third-party services, platforms, or truck manufacturers on this Site do not constitute an endorsement, partnership, or affiliation.
              </p>
            </Section>

            <Section id="changes" title="Changes to This Policy">
              <p>
                We may update this Copyright Policy as our Site content, tools, or legal obligations change. The "Last Updated" date at the top of this page reflects the most recent revision. Continued use of the Site constitutes acceptance of the current version of this Policy.
              </p>
            </Section>

            <Section id="contact" title="Contact">
              <p>
                For copyright licensing inquiries, permission requests, or DMCA notices, please contact us:
              </p>
              <div className="bg-[#0A0A0A]/[0.04] border border-border p-6 mt-2">
                <p className="font-head text-xs uppercase tracking-[0.25em] text-primary mb-3">Copyright & IP</p>
                <p className="text-sm">Andrej Sasaroga DBA Hylas Companion</p>
                <p className="text-sm mt-1">Email: <a href="mailto:contact@hylascompanion.com" className="text-primary hover:underline">contact@hylascompanion.com</a></p>
                <p className="text-sm mt-1">Subject Line: <em>"Copyright Inquiry"</em> or <em>"DMCA Notice"</em></p>
              </div>
            </Section>

          </article>
        </div>
      </div>
    </>
  );
}
